import {
  Alert,
  Box,
  Card,
  CardContent,
  Skeleton,
  Typography,
} from '@mui/material';
import { ArcElement, Chart, Tooltip as ChartTooltip } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { FIC_FIDC_CHART_COLORS } from 'src/utils/ficFidcChartColors';

import {
  formatConcentrationCompetency,
  formatConcentrationCurrency,
  formatConcentrationPercentage,
  wrapConcentrationTooltipLabel,
} from '../concentration/formatters';
import { getFicFidcCockpitConcentrationFeedback } from '../concentration/statusMessages';

import type { FicFidcCompositionCardViewModel } from '../concentration/contract';

Chart.register(ArcElement, ChartTooltip);

interface FicFidcCompositionCardProps {
  data: FicFidcCompositionCardViewModel;
  loading: boolean;
  testId: string;
}

export function FicFidcCompositionCard({
  data,
  loading,
  testId,
}: FicFidcCompositionCardProps) {
  const title = data.key === 'asset' ? 'Ativo-lastro' : 'Gestor';
  const chartItems = data.items;
  const state = loading ? 'loading' : data.status;
  const feedbackStatus = data.status === 'available' ? 'ready' : data.status;
  const feedback = getFicFidcCockpitConcentrationFeedback(
    feedbackStatus,
    data.reason,
  );
  const canRenderComposition =
    data.items.length > 0 &&
    (data.status === 'available' || data.status === 'partial');
  const isPartial = data.status === 'partial';

  return (
    <Card
      component="article"
      aria-label={`Concentração por ${title}`}
      data-state={state}
      data-testid={testId}
      variant="outlined"
      sx={{
        borderRadius: 1,
        display: 'flex',
        flexDirection: 'column',
        height: 'auto',
        minHeight: 232,
        minWidth: 0,
        overflow: 'visible',
      }}>
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          p: { xs: 1.5, sm: 2 },
        }}>
        <Box
          sx={{
            alignItems: 'flex-start',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1,
            mb: 1.5,
          }}>
          <Typography component="h3" fontWeight={700} variant="subtitle1">
            Concentração (por {title.toLocaleLowerCase('pt-BR')})
          </Typography>
          <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
            <Typography
              color="text.secondary"
              display="block"
              variant="caption">
              {formatConcentrationCompetency(data.competence) ||
                'Competência indisponível'}
            </Typography>
          </Box>
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Skeleton height={112} variant="circular" width={112} />
          </Box>
        ) : !canRenderComposition ? (
          <Alert
            data-testid={`${testId}-feedback`}
            role="status"
            severity={feedback?.severity ?? 'info'}
            variant="outlined">
            {feedback?.message ??
              'A concentração não está disponível para a competência selecionada.'}
          </Alert>
        ) : (
          <Box
            data-layout="column"
            sx={{
              display: 'flex',
              flex: '0 0 auto',
              flexDirection: 'column',
              gap: 1.5,
              alignItems: 'stretch',
              minHeight: 0,
              mt: 0,
            }}>
            {isPartial && (
              <Alert
                data-testid={`${testId}-feedback`}
                role="status"
                severity={feedback?.severity ?? 'warning'}
                sx={{ mb: 1, width: '100%' }}
                variant="outlined">
                {feedback?.message ??
                  'Os dados de concentração estão incompletos.'}
              </Alert>
            )}
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flex: '0 0 auto',
                justifyContent: 'center',
                minHeight: 0,
                minWidth: 0,
                overflow: 'visible',
                px: { xs: 1, sm: 2 },
                width: '100%',
              }}>
              <Box
                data-testid={`${testId}-donut`}
                sx={{
                  aspectRatio: '1 / 1',
                  flex: '0 0 auto',
                  height: { xs: 176, sm: 184 },
                  overflow: 'visible',
                  position: 'relative',
                  width: { xs: 176, sm: 184 },
                }}>
                <Doughnut
                  aria-label={`Distribuição por ${title}`}
                  data={{
                    labels: chartItems.map((item) => item.name),
                    datasets: [
                      {
                        data: chartItems.map((item) => item.percentage),
                        backgroundColor: chartItems.map(
                          (_, index) =>
                            FIC_FIDC_CHART_COLORS[
                              index % FIC_FIDC_CHART_COLORS.length
                            ],
                        ),
                        borderWidth: 0,
                      },
                    ],
                  }}
                  options={{
                    cutout: '70%',
                    events: [
                      'mousemove',
                      'mouseout',
                      'touchstart',
                      'touchmove',
                    ],
                    interaction: { intersect: true, mode: 'nearest' },
                    layout: { padding: 18 },
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        enabled: true,
                        caretPadding: 8,
                        callbacks: {
                          title: (contexts) =>
                            wrapConcentrationTooltipLabel(
                              chartItems[contexts[0]?.dataIndex]?.name ?? title,
                              16,
                            ),
                          label: (context) => {
                            const item = chartItems[context.dataIndex];
                            if (!item) return '';

                            return [
                              `Participação: ${formatConcentrationPercentage(item.percentage, 2)}`,
                              `Montante: ${formatConcentrationCurrency(item.vlMercPosFinal, 2)}`,
                            ];
                          },
                        },
                        padding: 8,
                        position: 'nearest',
                      },
                    },
                  }}
                />
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    inset: 0,
                    justifyContent: 'center',
                    pointerEvents: 'none',
                    position: 'absolute',
                  }}>
                  <Typography
                    fontSize={{ xs: 16, sm: 18 }}
                    fontWeight={600}
                    lineHeight={1.05}
                    sx={{ maxWidth: '72%' }}>
                    {isPartial ? 'Parcial' : '100%'}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    fontSize={{ xs: 9, sm: 10 }}
                    lineHeight={1.1}
                    sx={{ maxWidth: '82%' }}>
                    {isPartial ? 'cobertura' : 'do patrimônio'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
